import { Icon } from "@iconify/react";
import { AnimatePresence, motion, useInView } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const Color = {
  themeBlue: "#4ea4f5",
  textPrimary: "#0d0d0d",
  textSecondary: "#5a5a5d",
  textHintStrong: "#979797",
  surfacePrimary: "#ffffff",
  surfacePanel: "#fcfcfb",
  interactiveHover: "#f3f3f5",
  borderLight: "#c4c4c7",
  lineDivider: "#e5e5e5",
};

interface FeatureSectionAIDemoProps {
  showDescription?: boolean;
  autoStart?: boolean;
}

type DemoStage = "draft" | "sent" | "thinking" | "generated";

const toolOptions = [
  { label: "Mind Map", icon: "lucide:git-branch", color: "#3b82f6" },
  { label: "Video", icon: "lucide:video", color: "#14b8a6" },
  { label: "Podcast", icon: "lucide:mic", color: "#ef4444" },
  { label: "PDF", icon: "lucide:file-text", color: "#C62828" },
  { label: "PPT", icon: "lucide:presentation", color: "#D84315" },
  { label: "Images", icon: "lucide:image", color: "#8b5cf6" },
  { label: "HTML", icon: "lucide:file-code", color: "#546E7A" },
  { label: "Word", icon: "lucide:file-type", color: "#1565C0" },
];

const conversationArtifacts = [
  {
    filename: "Client Brief.pdf",
    meta: "PDF - 248 KB",
    icon: "ion:document-text-outline",
    color: "#C62828",
  },
  {
    filename: "Strategy Deck.pptx",
    meta: "PPTX - 1.8 MB",
    icon: "ion:easel-outline",
    color: "#D84315",
  },
  {
    filename: "Forecast Model.xlsx",
    meta: "Excel - 312 KB",
    icon: "ion:grid-outline",
    color: "#2E7D32",
  },
];

const studioScrollItems = [...toolOptions, ...toolOptions];

const FeatureSectionAIDemo: React.FC<FeatureSectionAIDemoProps> = ({
  showDescription = true,
  autoStart = false,
}) => {
  const { t } = useTranslation();

  const [showBulletPoints, setShowBulletPoints] = useState(autoStart);
  const [demoStage, setDemoStage] = useState<DemoStage>("draft");

  const cardRef = useRef<HTMLDivElement>(null);
  const bulletPointsShownRef = useRef<boolean>(autoStart);

  const inViewResult = useInView(cardRef, { amount: 0.3 });
  const isInView = autoStart || inViewResult;

  useEffect(() => {
    if (!isInView || bulletPointsShownRef.current) return;

    bulletPointsShownRef.current = true;
    const timer = window.setTimeout(() => setShowBulletPoints(true), 600);
    return () => window.clearTimeout(timer);
  }, [isInView]);

  useEffect(() => {
    if (!isInView) return;

    setDemoStage("draft");
    const sentDelay = autoStart ? 250 : 550;
    const thinkingDelay = autoStart ? 900 : 1250;
    const generatedDelay = autoStart ? 1700 : 2200;
    const timers = [
      window.setTimeout(() => setDemoStage("sent"), sentDelay),
      window.setTimeout(() => setDemoStage("thinking"), thinkingDelay),
      window.setTimeout(() => setDemoStage("generated"), generatedDelay),
    ];

    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [autoStart, isInView]);

  return (
    <section
      className={`${showDescription ? (autoStart ? "py-4 md:py-8" : "py-8 md:py-20") : "py-0"} bg-white overflow-hidden`}
    >
      <div className={showDescription ? "container mx-auto px-4" : ""}>
        <div
          className={`${showDescription ? "grid md:grid-cols-2 gap-10 lg:gap-14 items-stretch max-w-7xl mx-auto" : "flex justify-center"}`}
          style={showDescription ? { minHeight: "auto" } : undefined}
        >
          {showDescription && (
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              {...(autoStart
                ? { animate: { opacity: 1, x: 0 } }
                : { whileInView: { opacity: 1, x: 0 }, viewport: { once: true } })}
              transition={{ duration: 0.6 }}
              className="order-1 flex flex-col"
            >
              <div>
                <div
                  className="mb-6 inline-flex items-center gap-2 rounded-full px-4 py-2"
                  style={{ backgroundColor: `${Color.themeBlue}15` }}
                >
                  <Icon icon="lucide:sparkles" style={{ color: Color.themeBlue }} />
                  <span style={{ color: Color.themeBlue }} className="text-sm font-medium">
                    {t("modern.workflowDemo.badge")}
                  </span>
                </div>

                <h2 className="mb-6 mt-0 text-3xl font-bold leading-tight text-gray-900 md:text-5xl lg:text-7xl">
                  {t("modern.workflowDemo.headline")}
                </h2>

                <p className="text-lg leading-relaxed md:text-xl" style={{ color: "#726F6F" }}>
                  {t("modern.workflowDemo.description")}
                </p>
              </div>

              <div className="min-h-[16px] max-h-[32px] flex-1" />

              <div className="block space-y-4 md:hidden">
                {[
                  { icon: "lucide:upload", textKey: "modern.aiDemo.bullets.upload" },
                  { icon: "lucide:cpu", textKey: "modern.aiDemo.bullets.models" },
                  { icon: "lucide:message-square", textKey: "modern.aiDemo.bullets.citations" },
                ].map((item) => (
                  <div key={item.textKey} className="flex items-center gap-3">
                    <div
                      className="flex h-9 w-9 items-center justify-center rounded-lg"
                      style={{ backgroundColor: `${Color.themeBlue}15` }}
                    >
                      <Icon icon={item.icon} className="text-lg" style={{ color: Color.themeBlue }} />
                    </div>
                    <span className="text-base text-gray-700">{t(item.textKey)}</span>
                  </div>
                ))}
              </div>

              <div className="hidden md:block">
                <motion.div
                  className="space-y-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: showBulletPoints ? 1 : 0 }}
                  transition={{ duration: 0.4 }}
                >
                  {[
                    { icon: "lucide:upload", textKey: "modern.aiDemo.bullets.upload" },
                    { icon: "lucide:cpu", textKey: "modern.aiDemo.bullets.models" },
                    { icon: "lucide:message-square", textKey: "modern.aiDemo.bullets.citations" },
                  ].map((item, index) => (
                    <motion.div
                      key={item.textKey}
                      className="flex items-center gap-3"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{
                        opacity: showBulletPoints ? 1 : 0,
                        x: showBulletPoints ? 0 : -20,
                      }}
                      transition={{ delay: showBulletPoints ? index * 0.15 : 0, duration: 0.4 }}
                    >
                      <div
                        className="flex h-9 w-9 items-center justify-center rounded-lg"
                        style={{ backgroundColor: `${Color.themeBlue}15` }}
                      >
                        <Icon icon={item.icon} className="text-lg" style={{ color: Color.themeBlue }} />
                      </div>
                      <span className="text-base text-gray-700">{t(item.textKey)}</span>
                    </motion.div>
                  ))}
                </motion.div>
              </div>
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0, x: showDescription ? 50 : 0 }}
            {...(autoStart
              ? { animate: { opacity: 1, x: 0 } }
              : { whileInView: { opacity: 1, x: 0 }, viewport: { once: true } })}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="order-2 flex flex-col px-4 md:px-0"
            style={{ paddingTop: "68px" }}
          >
            <div className="relative mx-auto w-full max-w-[620px]">
              <div
                ref={cardRef}
                className="relative min-h-[470px] overflow-hidden rounded-[24px] shadow-[0_4px_10px_rgba(0,0,0,0.04)] sm:min-h-[430px]"
                style={{
                  backgroundColor: Color.surfacePrimary,
                  border: `1px solid ${Color.borderLight}`,
                }}
              >
                <div className="absolute inset-0 flex flex-col bg-white">
                  <div className="flex min-h-0 flex-1">
                    <div className="flex w-[56%] min-w-0 flex-col bg-white">
                      <div className="min-h-0 flex-1 overflow-hidden px-3 py-3 sm:px-4">
                        {demoStage !== "draft" ? (
                          <motion.div
                            animate={{ opacity: 1, y: 0 }}
                            className="mb-4 flex justify-end"
                            initial={{ opacity: 0, y: 8 }}
                            transition={{ duration: 0.25 }}
                          >
                            <div
                              className="max-w-[92%] rounded-[18px] px-3 py-2 text-left text-[12px] leading-[17px] sm:max-w-[88%] sm:text-[13px] sm:leading-[18px]"
                              style={{ backgroundColor: `${Color.themeBlue}20`, color: Color.textPrimary }}
                            >
                              Generate the client brief, deck, web page, and export files.
                            </div>
                          </motion.div>
                        ) : null}

                        <AnimatePresence mode="wait">
                          {demoStage === "thinking" ? (
                            <motion.div
                              animate={{ opacity: 1, y: 0 }}
                              className="max-w-[92%]"
                              exit={{ opacity: 0, y: -6 }}
                              initial={{ opacity: 0, y: 8 }}
                              key="thinking"
                              transition={{ duration: 0.2 }}
                            >
                              <div className="px-0 py-1">
                                <div className="mb-2 flex items-center gap-2">
                                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#4ea4f5]" />
                                  <span className="text-[11px] font-semibold leading-4 text-[#0d0d0d] sm:text-[12px]">
                                    Building outputs
                                  </span>
                                </div>
                                <div className="flex gap-1">
                                  {[0, 1, 2].map((index) => (
                                    <motion.span
                                      animate={{ opacity: [0.35, 1, 0.35] }}
                                      className="h-1.5 w-1.5 rounded-full bg-[#4ea4f5]"
                                      key={index}
                                      transition={{ delay: index * 0.16, duration: 0.9, repeat: Infinity }}
                                    />
                                  ))}
                                </div>
                              </div>
                            </motion.div>
                          ) : null}

                          {demoStage === "generated" ? (
                            <motion.div
                              animate={{ opacity: 1, y: 0, scale: 1 }}
                              className="min-w-0"
                              exit={{ opacity: 0, y: -10, scale: 0.98 }}
                              initial={{ opacity: 0, y: 8, scale: 0.98 }}
                              key="generated-files"
                              transition={{ duration: 0.22 }}
                            >
                              <div className="min-w-0 flex-1">
                                <div className="mb-2 px-0 py-1">
                                  <p className="text-[11px] font-semibold leading-4 text-[#0d0d0d] sm:text-[12px]">
                                    Generated demo files.
                                  </p>
                                </div>
                                <div className="flex flex-col gap-1.5">
                                  {conversationArtifacts.map((artifact, index) => (
                                    <motion.div
                                      animate={{ opacity: 1, y: 0, scale: 1 }}
                                      className="group flex min-h-[48px] w-full items-center rounded-[10px] border-0 bg-transparent pr-2 text-left"
                                      initial={{ opacity: 0, y: 7, scale: 0.98 }}
                                      key={artifact.filename}
                                      transition={{ delay: 0.04 + index * 0.09, duration: 0.22 }}
                                    >
                                      <span className="flex min-h-[48px] min-w-0 flex-1 items-center px-2 py-1.5">
                                        <span className="mr-2 flex h-8 w-7 shrink-0 items-center justify-center rounded-[8px] bg-[#f8f8f7]">
                                          <Icon
                                            icon={artifact.icon}
                                            className="text-[15px]"
                                            style={{ color: artifact.color }}
                                          />
                                        </span>
                                        <span className="min-w-0 flex-1">
                                          <span className="block truncate text-[10px] font-semibold leading-3 text-[#0d0d0d] sm:text-[11px]">
                                            {artifact.filename}
                                          </span>
                                          <span className="mt-0.5 block truncate text-[9px] leading-3 text-[#7d7d80]">
                                            {artifact.meta}
                                          </span>
                                        </span>
                                      </span>
                                      <Icon icon="ion:chevron-forward" className="ml-1 h-3.5 w-3.5 shrink-0 text-[#7d7d80]" />
                                    </motion.div>
                                  ))}
                                </div>
                              </div>
                            </motion.div>
                          ) : null}
                        </AnimatePresence>
                      </div>

                      <div className="shrink-0 border-t bg-white px-3 py-3" style={{ borderColor: Color.lineDivider }}>
                        <div
                          className="flex min-h-[46px] w-full items-center gap-2 rounded-[20px] border bg-white px-3 text-left shadow-[0_4px_10px_rgba(0,0,0,0.04)]"
                          style={{ borderColor: Color.borderLight }}
                        >
                          <span className="min-w-0 flex-1 truncate text-[12px] text-[#979797] sm:text-[13px]">
                            {t("modern.aiAgent.placeholder")}
                          </span>
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#c4c4c7] bg-white opacity-60">
                            <Icon icon="ion:arrow-up" className="text-[16px] text-[#4ea4f5]" />
                          </span>
                        </div>
                      </div>
                    </div>

                    <div
                      className="flex w-[44%] min-w-0 flex-col border-l"
                      style={{ backgroundColor: Color.surfacePanel, borderColor: Color.lineDivider }}
                    >
                      <div className="flex h-10 shrink-0 items-center gap-1.5 border-b bg-white px-3" style={{ borderColor: Color.lineDivider }}>
                        <Icon icon="ion:grid-outline" className="text-[13px]" style={{ color: Color.themeBlue }} />
                        <span className="truncate text-[11px] font-semibold leading-4 text-[#666]">
                          Studio
                        </span>
                      </div>

                      <div className="relative min-h-0 flex-1 overflow-hidden p-2">
                        <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-8 bg-gradient-to-b from-[#fcfcfb] to-transparent" />
                        <motion.div
                          animate={{ y: ["0%", "-26%", "-26%"] }}
                          className="grid grid-cols-2 content-start gap-2 pb-2"
                          data-testid="studio-scroll-list"
                          transition={{ duration: 8.5, ease: "easeInOut", repeat: Infinity, repeatDelay: 1.2, times: [0, 0.72, 1] }}
                        >
                          {studioScrollItems.map((tool, index) => (
                            <motion.div
                              animate={{ opacity: 1, y: 0 }}
                              className="flex min-h-[58px] flex-col items-center justify-center rounded-[12px] border bg-white px-1.5 py-2 text-center"
                              initial={{ opacity: 0, y: 8 }}
                              key={`${tool.label}-${index}`}
                              style={{ borderColor: Color.lineDivider }}
                              transition={{ delay: (index % toolOptions.length) * 0.035, duration: 0.18 }}
                            >
                              <Icon icon={tool.icon} className="mb-1 text-[16px]" style={{ color: tool.color }} />
                              <span className="text-[9px] font-semibold leading-[11px] text-[#555] sm:text-[10px] sm:leading-3">
                                {tool.label}
                              </span>
                            </motion.div>
                          ))}
                        </motion.div>
                        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-8 bg-gradient-to-t from-[#fcfcfb] to-transparent" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default FeatureSectionAIDemo;
